import {
  Agency,
  AgencySortBy,
  AgencySortDirection,
  ConnectAgencyClientDto,
  CreateAgencyDto,
  CreateAgencyResponse,
  GetAgencyClientsParams,
  GetAgencyClientsResponse,
  GetAgencyUsersParams,
  GetAgencyUsersResponse,
  GetLinkableClientsParams,
  GetLinkableClientsResponse,
  InviteAgencyUserDto,
  InviteAgencyUserResponse,
  UpdateAgencyDto,
  UpdateAgencyUserDto,
} from "@/types";
import { axiosInstance } from "@/lib/axios";

export interface GetAgenciesParams {
  /** 1-based, matching the API; the table's page index is 0-based. */
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  sortBy?: AgencySortBy;
  direction?: AgencySortDirection;
}

export interface GetAgenciesResponse {
  data: Agency[];
  count: number;
}

/**
 * Get all agencies with pagination
 */
export const getAgencies = async (
  params?: GetAgenciesParams,
): Promise<GetAgenciesResponse> => {
  const response = await axiosInstance.get<GetAgenciesResponse>("/agencies", {
    params,
  });
  return response.data;
};

/**
 * Get a single agency by ID
 *
 * API: GET /agencies/{agencyId}/info
 */
export const getAgencyById = async (agencyId: string): Promise<Agency> => {
  const response = await axiosInstance.get<Agency>(
    `/agencies/${agencyId}/info`,
  );
  return response.data;
};

/**
 * Create a new agency
 */
export const createAgency = async (
  data: CreateAgencyDto,
): Promise<CreateAgencyResponse> => {
  const response = await axiosInstance.post<CreateAgencyResponse>(
    "/agencies",
    data,
  );
  return response.data;
};

/**
 * Rename an existing agency
 */
export const updateAgency = async (
  agencyId: string,
  data: UpdateAgencyDto,
): Promise<Agency> => {
  const response = await axiosInstance.patch<Agency>(
    `/agencies/${agencyId}`,
    data,
  );
  return response.data;
};

/**
 * Delete an agency, together with its people, its content trips and every
 * deliverable and asset under those trips.
 *
 * NOT refused for a non-empty agency any more — the endpoint used to answer 409
 * with `AGENCY_HAS_MEMBERS` or `AGENCY_HAS_CONTENT_TRIPS` and raises neither
 * now, so the confirmation in the UI is the only gate. An unknown agency is
 * still a 404.
 */
export const deleteAgency = async (agencyId: string): Promise<void> => {
  await axiosInstance.delete(`/agencies/${agencyId}`);
};

/**
 * Get the people in an agency with pagination
 * API: GET /agencies/{agencyId}/users
 */
export const getAgencyUsers = async (
  agencyId: string,
  params?: GetAgencyUsersParams,
): Promise<GetAgencyUsersResponse> => {
  const response = await axiosInstance.get<GetAgencyUsersResponse>(
    `/agencies/${agencyId}/users`,
    { params },
  );
  return response.data;
};

/**
 * Invite people into an agency (supports several invitations at once).
 *
 * A single invitee failing is reported on its own row rather than failing the
 * batch, so this resolves 2xx with `failureCount` set.
 * API: POST /agencies/{agencyId}/users/invite
 */
export const inviteAgencyUsers = async (
  agencyId: string,
  data: InviteAgencyUserDto,
): Promise<InviteAgencyUserResponse> => {
  const response = await axiosInstance.post<InviteAgencyUserResponse>(
    `/agencies/${agencyId}/users/invite`,
    data,
  );
  return response.data;
};

/**
 * Change an agency person's role.
 *
 * ⚠️ The same route venue-app's agency settings use. Swagger documents it as
 * session-scoped: the agency is the CALLER's own, and no agency id is sent or
 * may be added — hence none here. Used from the admin console at the user's
 * request (2026-09-28); whether the backend accepts an admin-console session
 * on it is not confirmed by the contract.
 *
 * 409 with `LAST_AGENCY_ADMIN` when demoting the only active admin.
 * API: PATCH /agencies/users/{userId}
 */
export const updateAgencyUser = async (
  userId: string,
  data: UpdateAgencyUserDto,
): Promise<void> => {
  await axiosInstance.patch(`/agencies/users/${userId}`, data);
};

/**
 * Remove a person from an agency, or withdraw a pending invitation.
 *
 * ⚠️ Session-scoped, like {@link updateAgencyUser} — the same route venue-app's
 * agency settings use, with no agency id, used here at the user's request.
 *
 * 409 with `LAST_AGENCY_ADMIN` when removing the only active admin.
 * API: DELETE /agencies/users/{userId}
 */
export const removeAgencyUser = async (userId: string): Promise<void> => {
  await axiosInstance.delete(`/agencies/users/${userId}`);
};

/**
 * Get the venues an agency works for, with pagination.
 *
 * Takes NO search parameter. The search box that sits above this table on the
 * agency detail screen belongs to `getLinkableClients` and does something else
 * entirely — it looks across every venue rather than filtering this list.
 * API: GET /agencies/{agencyId}/clients
 */
export const getAgencyClients = async (
  agencyId: string,
  params?: GetAgencyClientsParams,
  signal?: AbortSignal,
): Promise<GetAgencyClientsResponse> => {
  const response = await axiosInstance.get<GetAgencyClientsResponse>(
    `/agencies/${agencyId}/clients`,
    { params, signal },
  );
  return response.data;
};

/**
 * Search the venues this agency could be connected to.
 *
 * The endpoint restricts to marketing-enabled venues and excludes the ones
 * already connected to this agency, both server-side. DO NOT RE-FILTER THE
 * RESULTS IN THE BROWSER — a second pass here would silently drop rows the
 * server deliberately offered.
 * API: GET /agencies/{agencyId}/linkable-clients
 */
export const getLinkableClients = async (
  agencyId: string,
  params?: GetLinkableClientsParams,
  signal?: AbortSignal,
): Promise<GetLinkableClientsResponse> => {
  const response = await axiosInstance.get<GetLinkableClientsResponse>(
    `/agencies/${agencyId}/linkable-clients`,
    { params, signal },
  );
  return response.data;
};

/**
 * Connect a venue to an agency.
 *
 * 201 on success. The named refusals are the server's to make and the caller
 * renders them rather than pre-checking: 404 for a venue that does not exist,
 * 400 for one without marketing switched on, 409 for a pair already connected.
 * API: POST /agencies/{agencyId}/clients
 */
export const connectAgencyClient = async (
  agencyId: string,
  data: ConnectAgencyClientDto,
): Promise<void> => {
  await axiosInstance.post(`/agencies/${agencyId}/clients`, data);
};

/**
 * Disconnect a venue from an agency, deleting every content trip this agency
 * ran for it along with the deliverables, photos and comments inside them.
 * Photos already delivered into the venue's own Library are kept.
 *
 * ANSWERS 202, NOT 204 — accepted, not finished. Resolving here means the job
 * was queued, not that the work is done, so the caller must not treat it as a
 * completed delete. The backend removes the connection row LAST, which makes
 * the row vanishing from `getAgencyClients` the truthful "this finished"
 * signal; poll that list rather than looking for a status field, because there
 * is none.
 * API: DELETE /agencies/{agencyId}/clients/{venueId}
 */
export const disconnectAgencyClient = async (
  agencyId: string,
  venueId: string,
): Promise<void> => {
  await axiosInstance.delete(`/agencies/${agencyId}/clients/${venueId}`);
};
